import * as RadixPopover from '@radix-ui/react-popover';
import cn from 'classnames';
import { ChangeEvent, FC, useCallback, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useLocation, useNavigate, useSearchParams } from 'react-router';

import { PATHS } from '@/shared/config';
import { useAppDispatch, useAppSelector } from '@/shared/hooks';
import {
  buildTemplatesUrl,
  getLanguageDisplayName,
  getName,
} from '@/shared/lib';
import {
  Button,
  Checkbox,
  DropdownSelect,
  Icon,
  Select,
  toastCaller,
} from '@/shared/ui';
import { ValueType } from '@/shared/ui/Select/Select.types';

import { ModalID, modalModel } from '@/entities/modal';
import { organizationModel } from '@/entities/organization';
import {
  createTemplatesNavigationState,
  DocumentTemplateSummary,
  DocumentType,
  getPathWithSearch,
  getTemplateInclusion,
  resolveTemplatesBackPath,
  templatesModel,
  VersionStatus,
} from '@/entities/templates';
import { listLanguagesWithSelected } from '@/entities/transcriptor';
import { userModel } from '@/entities/user';

import { templateEditorMessages } from '@/features/templateEditor/config/i18n';

import {
  LANGUAGE_FILTER_ALL,
  TEMPLATE_LIST_PARAM,
  templateListMessages,
} from '../../config';
import {
  useTemplateLanguageFilter,
  useTemplateNameColumnWidth,
} from '../../hooks';
import {
  filterTemplateDataByLanguage,
  getTemplateListEmptyMessage,
  getTemplateSpecialtyGroups,
  matchesLanguageFilter,
} from '../../lib';

import styles from './DocumentTemplateList.module.scss';

type DocumentTemplateListProps = {
  className?: string;
};

const TYPE_OPTIONS: Array<{ label: string; value: DocumentType | '' }> = [
  { label: 'All', value: '' },
  { label: 'summary', value: 'SUMMARY' },
  { label: 'evaluation', value: 'EVALUATION' },
  { label: 'protocol', value: 'PROTOCOL' },
  { label: 'literature_review', value: 'LITERATURE_REVIEW' },
  { label: 'follow_up_letter', value: 'FOLLOW_UP_LETTER' },
];

const STATUS_OPTIONS: Array<{ label: string; value: VersionStatus | '' }> = [
  { label: 'All', value: '' },
  { label: 'draft', value: 'draft' },
  { label: 'active', value: 'active' },
  { label: 'deleted', value: 'deleted' },
];

const formatDate = (locale: string, dateString?: string | null): string => {
  if (!dateString) {
    return '—';
  }

  const date = new Date(dateString);

  if (Number.isNaN(date.getTime())) {
    return '—';
  }

  return date.toLocaleDateString(locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const getStatusClassName = (status: VersionStatus): string => {
  switch (status) {
    case 'active':
      return styles.statusCurrent;
    case 'deleted':
      return styles.statusDeleted;
    case 'draft':
    default:
      return styles.statusDraft;
  }
};

export const DocumentTemplateList: FC<DocumentTemplateListProps> = (props) => {
  const { className } = props;

  const { formatMessage, locale } = useIntl();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();

  const allTemplates = useAppSelector(
    templatesModel.selectors.selectAllDocumentTemplates,
  );
  const allSchemas = useAppSelector(
    templatesModel.selectors.selectEncounterSchemas,
  );
  const encounterSchemasListError = useAppSelector(
    templatesModel.selectors.selectEncounterSchemasListError,
  );
  const allUsers = useAppSelector(organizationModel.selectors.selectAllUsers);
  const userLocale = useAppSelector(userModel.selectors.selectUserLocale);

  const {
    availableLanguages,
    language: languageFilter,
    setLanguage,
  } = useTemplateLanguageFilter();
  const { width: nameColumnWidth, startResize: startNameColumnResize } =
    useTemplateNameColumnWidth();
  const { schemas, templates } = useMemo(
    () =>
      filterTemplateDataByLanguage({
        language: languageFilter,
        schemas: allSchemas,
        templates: allTemplates,
      }),
    [allSchemas, allTemplates, languageFilter],
  );

  const [typeFilter, setTypeFilter] = useState<DocumentType | ''>('');
  const [statusFilter, setStatusFilter] = useState<VersionStatus | ''>('');
  const [showDeleted, setShowDeleted] = useState(false);
  const [addMenuOpen, setAddMenuOpen] = useState(false);

  const usersById = useMemo(() => {
    const map = new Map<string, (typeof allUsers)[number]>();

    (allUsers ?? []).forEach((user) => {
      if (user?.UserID) {
        map.set(user.UserID, user);
      }
    });

    return map;
  }, [allUsers]);

  const resolveAuthorName = useCallback(
    (template: DocumentTemplateSummary): string => {
      if (template.updated_by_display_name) {
        return template.updated_by_display_name;
      }

      if (template.author_display_name) {
        return template.author_display_name;
      }

      const candidateId = template.updated_by ?? template.created_by ?? null;
      if (!candidateId) {
        return '—';
      }

      const user = usersById.get(candidateId);
      if (!user) {
        return '—';
      }

      const fullName = getName(
        user.PersonalData?.FirstName,
        user.PersonalData?.LastName,
        { userLocale },
      ).trim();

      return fullName || '—';
    },
    [usersById, userLocale],
  );

  const selectedSchemaId = searchParams.get(TEMPLATE_LIST_PARAM.specialty);
  const templatesBackPath = resolveTemplatesBackPath(
    location.state,
    getPathWithSearch(location.pathname, location.search),
  );

  const { groups } = useMemo(
    () => getTemplateSpecialtyGroups(schemas, templates),
    [schemas, templates],
  );

  const selectedGroup = useMemo(
    () =>
      selectedSchemaId
        ? groups.find(({ schema }) => schema.id === selectedSchemaId)
        : undefined,
    [groups, selectedSchemaId],
  );
  const selectedSchema = selectedGroup?.schema;
  const getDocumentTypeLabel = useCallback(
    (type: DocumentType | '' | undefined): string => {
      switch (type) {
        case '':
          return formatMessage(templateListMessages.all);
        case 'SUMMARY':
          return formatMessage(templateEditorMessages.docTypeSummary);
        case 'EVALUATION':
          return formatMessage(templateEditorMessages.docTypeEvaluation);
        case 'PROTOCOL':
          return formatMessage(templateEditorMessages.docTypeProtocol);
        case 'FOLLOW_UP_LETTER':
          return formatMessage(templateEditorMessages.docTypeFollowUpLetter);
        case 'LITERATURE_REVIEW':
          return formatMessage(templateEditorMessages.docTypeLiteratureReview);
        default:
          return (type ?? '')
            .toLowerCase()
            .split('_')
            .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
            .join(' ');
      }
    },
    [formatMessage],
  );
  const getStatusLabel = useCallback(
    (status: VersionStatus): string => {
      switch (status) {
        case 'draft':
          return formatMessage(templateEditorMessages.statusDraftLabel);
        case 'active':
          return formatMessage(templateListMessages.statusActive);
        case 'deleted':
          return formatMessage(templateListMessages.statusDeleted);
        default:
          return status;
      }
    },
    [formatMessage],
  );
  const typeFilterOptions = useMemo(
    () =>
      TYPE_OPTIONS.map((option) => ({
        value: option.value,
        label: getDocumentTypeLabel(option.value),
      })),
    [getDocumentTypeLabel],
  );
  const languageFilterOptions = [
    {
      value: LANGUAGE_FILTER_ALL,
      label: formatMessage(templateListMessages.all),
    },
    ...listLanguagesWithSelected(availableLanguages, languageFilter).map(
      (code) => ({
        value: code,
        label: getLanguageDisplayName(code, locale),
      }),
    ),
  ];
  const statusFilterOptions = useMemo(
    () =>
      STATUS_OPTIONS.map((option) => ({
        value: option.value,
        label: option.value
          ? getStatusLabel(option.value)
          : formatMessage(templateListMessages.all),
      })),
    [formatMessage, getStatusLabel],
  );

  const sourceTemplates = useMemo(() => {
    if (!selectedSchemaId) {
      return templates;
    }

    return selectedGroup?.templates ?? [];
  }, [selectedGroup, selectedSchemaId, templates]);

  const filteredTemplates = useMemo(
    () =>
      sourceTemplates.filter((template) => {
        if (typeFilter && template.type !== typeFilter) {
          return false;
        }

        if (statusFilter && template.status !== statusFilter) {
          return false;
        }

        if (template.status === 'archived') {
          return false;
        }

        if (!showDeleted && template.status === 'deleted') {
          return false;
        }

        return true;
      }),
    [showDeleted, sourceTemplates, statusFilter, typeFilter],
  );

  const handleCreateTemplate = () => {
    setAddMenuOpen(false);
    navigate(
      buildTemplatesUrl({
        editor: 'document',
        mode: 'new',
        specialty: selectedSchemaId ?? undefined,
      }),
      { state: createTemplatesNavigationState(templatesBackPath) },
    );
  };

  const handleImportTemplate = () => {
    setAddMenuOpen(false);
    navigate(
      buildTemplatesUrl({
        editor: 'document',
        mode: 'new',
        specialty: selectedSchemaId ?? undefined,
      }),
      {
        state: {
          autoOpenImport: true,
          ...createTemplatesNavigationState(templatesBackPath),
        },
      },
    );
  };

  const handleEditTemplate = (
    event: React.MouseEvent,
    template: DocumentTemplateSummary,
  ) => {
    event.stopPropagation();
    navigate(
      buildTemplatesUrl({
        editor: 'document',
        mode: 'edit',
        id: template.id,
        specialty: selectedSchemaId ?? undefined,
      }),
      { state: createTemplatesNavigationState(templatesBackPath) },
    );
  };

  const handleDeleteTemplate = (
    event: React.MouseEvent,
    template: DocumentTemplateSummary,
  ) => {
    event.stopPropagation();

    if (!template.id) {
      return;
    }

    dispatch(
      modalModel.actions.openModal({
        modalID: ModalID.Confirm,
        data: {
          title: formatMessage(templateListMessages.deleteConfirm),
          description: formatMessage(
            templateListMessages.deleteConfirmDescription,
          ),
          okText: formatMessage(templateListMessages.deleteConfirmAction),
          danger: true,
          onConfirm: async () => {
            dispatch(modalModel.actions.closeModal(ModalID.Confirm));
            try {
              await dispatch(
                templatesModel.deleteDocumentTemplateThunk(template.id),
              ).unwrap();
              toastCaller({
                type: 'success',
                message: formatMessage(templateListMessages.deleteSuccess),
              });
            } catch (error) {
              toastCaller({
                type: 'error',
                message:
                  error instanceof Error && error.message
                    ? error.message
                    : formatMessage(templateListMessages.deleteError),
              });
            }
          },
        },
      }),
    );
  };

  const hasActiveFilters = Boolean(
    typeFilter ||
      statusFilter ||
      showDeleted ||
      languageFilter !== LANGUAGE_FILTER_ALL,
  );
  const handleTypeFilterChange = (newValue: ChangeEvent | ValueType) => {
    if (typeof newValue === 'string') {
      setTypeFilter(newValue as DocumentType | '');
      return;
    }

    if (newValue === null) {
      setTypeFilter('');
    }
  };

  const handleStatusFilterChange = (newValue: ChangeEvent | ValueType) => {
    if (typeof newValue === 'string') {
      setStatusFilter(newValue as VersionStatus | '');
      return;
    }

    if (newValue === null) {
      setStatusFilter('');
    }
  };

  const handleLanguageFilterChange = (newValue: string | string[]) => {
    if (typeof newValue !== 'string') {
      return;
    }

    const selectedSchemaLanguage = allSchemas.find(
      ({ id }) => id === selectedSchemaId,
    )?.language;

    setLanguage(newValue, {
      resetSpecialty:
        Boolean(selectedSchemaId) &&
        !matchesLanguageFilter(newValue, selectedSchemaLanguage),
    });
  };

  const handleCheckboxChange = (
    value: boolean | ChangeEvent<Element>,
  ): boolean =>
    typeof value === 'boolean'
      ? value
      : (value.target as HTMLInputElement).checked;
  const showGenerationColumn = Boolean(selectedSchema);
  const totalColumns = showGenerationColumn ? 7 : 6;
  const rangeStart = filteredTemplates.length > 0 ? 1 : 0;
  const rangeEnd = filteredTemplates.length;
  const isSelectedSpecialtyMissing = Boolean(
    selectedSchemaId && !selectedGroup,
  );
  const emptyMessage = getTemplateListEmptyMessage({
    hasActiveFilters,
    hasSelectedSpecialty: Boolean(selectedSchemaId),
    hasSpecialtyTemplates: sourceTemplates.length > 0,
    isSelectedSpecialtyHiddenByFilter:
      isSelectedSpecialtyMissing &&
      allSchemas.some(({ id }) => id === selectedSchemaId),
    isSelectedSpecialtyUnavailable:
      isSelectedSpecialtyMissing && Boolean(encounterSchemasListError),
  });

  const handleEditSpecialty = () => {
    if (!selectedSchema?.id) {
      return;
    }

    navigate(
      buildTemplatesUrl({
        editor: 'schema',
        mode: 'edit',
        id: selectedSchema.id,
      }),
      { state: createTemplatesNavigationState(templatesBackPath) },
    );
  };

  return (
    <div
      className={cn(styles.container, className)}
      data-section="document-template-list"
    >
      <div className={styles.header}>
        <h1 className={cn(styles.title, 'h4')}>
          {selectedSchema?.name ?? formatMessage(templateListMessages.title)}
        </h1>

        <div className={styles.headerActions}>
          {selectedSchema?.id ? (
            <Button
              variant="secondary"
              size="medium"
              icon="pen"
              onClick={handleEditSpecialty}
            >
              {formatMessage(templateListMessages.editSpecialty)}
            </Button>
          ) : null}

          <Button
            variant="secondary"
            size="medium"
            onClick={() => navigate(PATHS.community)}
          >
            <FormattedMessage
              id="community.pageTitle"
              defaultMessage="Community"
            />
          </Button>

          <RadixPopover.Root open={addMenuOpen} onOpenChange={setAddMenuOpen}>
            <RadixPopover.Trigger asChild>
              <Button
                variant="primary"
                size="medium"
                icon="plus"
                className={styles.createButton}
              >
                {formatMessage(templateListMessages.addTemplate)}
              </Button>
            </RadixPopover.Trigger>
            <RadixPopover.Portal>
              <RadixPopover.Content
                className={styles.addMenuContent}
                sideOffset={8}
                align="start"
                collisionPadding={{ left: 32, right: 32 }}
              >
                <div className={styles.addMenu}>
                  <div
                    className={cn(styles.addMenuItem, 'p2')}
                    onClick={handleImportTemplate}
                  >
                    <Icon name="file" size={20} />
                    <span>
                      {formatMessage(templateListMessages.importTemplate)}
                    </span>
                  </div>
                  <div
                    className={cn(styles.addMenuItem, 'p2')}
                    onClick={handleCreateTemplate}
                  >
                    <Icon name="plus" size={20} />
                    <span>{formatMessage(templateListMessages.createNew)}</span>
                  </div>
                </div>
              </RadixPopover.Content>
            </RadixPopover.Portal>
          </RadixPopover.Root>
        </div>
      </div>

      <div className={styles.controlsBar}>
        <div className={styles.controls}>
          <Select
            className={styles.controlSelect}
            isSearchable={false}
            label={formatMessage(templateListMessages.type)}
            value={typeFilter}
            options={typeFilterOptions}
            menuPortalTarget={document.body}
            onChange={handleTypeFilterChange}
          />
          <Select
            className={styles.controlSelect}
            isSearchable={false}
            label={formatMessage(templateListMessages.status)}
            value={statusFilter}
            options={statusFilterOptions}
            menuPortalTarget={document.body}
            onChange={handleStatusFilterChange}
          />
          <DropdownSelect
            triggerClassName={styles.languageFilterTrigger}
            label={formatMessage(templateListMessages.language)}
            options={languageFilterOptions}
            value={languageFilter}
            onChange={handleLanguageFilterChange}
          />

          <div className={styles.toggleGroup}>
            <Checkbox
              checked={showDeleted}
              className={styles.controlCheckbox}
              onChange={(value) => setShowDeleted(handleCheckboxChange(value))}
            >
              {formatMessage(templateListMessages.showDeleted)}
            </Checkbox>
          </div>
        </div>

        <span className={cn('p3', styles.itemCount)}>
          {formatMessage(templateListMessages.showingItems, {
            from: rangeStart,
            to: rangeEnd,
            count: rangeEnd,
          })}
        </span>
      </div>

      <div className={styles.tableContainer}>
        <table className={styles.table}>
          <colgroup>
            <col
              className={styles.colName}
              style={
                nameColumnWidth !== null
                  ? { width: `${nameColumnWidth}px` }
                  : undefined
              }
            />
            <col className={styles.colAuthor} />
            <col className={styles.colLanguage} />
            <col className={styles.colModified} />
            {showGenerationColumn && <col className={styles.colGeneration} />}
            <col className={styles.colStatus} />
            <col className={styles.colType} />
          </colgroup>
          <thead className={styles.tableHeader}>
            <tr>
              <th
                className={cn(
                  styles.tableHeaderColumn,
                  styles.tableHeaderPrimary,
                  'p2',
                )}
              >
                {formatMessage(templateListMessages.name)}
                <span
                  aria-hidden
                  data-component="template-name-column-resizer"
                  className={styles.resizeHandle}
                  onPointerDown={startNameColumnResize}
                />
              </th>
              <th className={cn(styles.tableHeaderColumn, 'p2')}>
                {formatMessage(templateListMessages.author)}
              </th>
              <th className={cn(styles.tableHeaderColumn, 'p2')}>
                {formatMessage(templateListMessages.language)}
              </th>
              <th className={cn(styles.tableHeaderColumn, 'p2')}>
                {formatMessage(templateListMessages.modified)}
              </th>
              {showGenerationColumn && (
                <th
                  className={cn(
                    styles.tableHeaderColumn,
                    styles.tableHeaderCenter,
                    'p2',
                  )}
                >
                  {formatMessage(templateListMessages.generation)}
                </th>
              )}
              <th
                className={cn(
                  styles.tableHeaderColumn,
                  styles.tableHeaderCenter,
                  'p2',
                )}
              >
                {formatMessage(templateListMessages.status)}
              </th>
              <th className={cn(styles.tableHeaderColumn, 'p2')}>
                {formatMessage(templateListMessages.type)}
              </th>
            </tr>
          </thead>
          <tbody>
            {filteredTemplates.length === 0 ? (
              <tr>
                <td
                  className={cn(styles.emptyCell, 'p2')}
                  colSpan={totalColumns}
                >
                  {formatMessage(emptyMessage)}
                </td>
              </tr>
            ) : (
              filteredTemplates.map((template) => {
                const status = template.status ?? 'draft';
                const inclusion = selectedSchema
                  ? getTemplateInclusion(template, selectedSchema)
                  : undefined;
                const authorName = resolveAuthorName(template);
                const languageName = template.language
                  ? getLanguageDisplayName(template.language, locale)
                  : '—';

                return (
                  <tr
                    key={template.id}
                    className={cn(styles.tableRow, 'p2')}
                    onClick={() =>
                      navigate(
                        buildTemplatesUrl({
                          editor: 'document',
                          mode: 'view',
                          id: template.id,
                          specialty: selectedSchemaId ?? undefined,
                        }),
                        {
                          state:
                            createTemplatesNavigationState(templatesBackPath),
                        },
                      )
                    }
                  >
                    <td className={styles.tableCell}>
                      <span
                        className={cn(styles.nameLink, styles.cellText)}
                        title={template.name}
                      >
                        {template.name}
                      </span>
                    </td>
                    <td className={cn(styles.tableCell, styles.metaCell)}>
                      <span
                        data-ph-mask
                        className={styles.cellText}
                        title={authorName}
                      >
                        {authorName}
                      </span>
                    </td>
                    <td className={cn(styles.tableCell, styles.metaCell)}>
                      <span className={styles.cellText} title={languageName}>
                        {languageName}
                      </span>
                    </td>
                    <td className={cn(styles.tableCell, styles.metaCell)}>
                      {formatDate(
                        locale,
                        template.updated_at ?? template.created_at,
                      )}
                    </td>
                    {showGenerationColumn && (
                      <td className={cn(styles.tableCell, styles.centerCell)}>
                        {inclusion ? (
                          <span className={cn('p4', styles.generationBadge)}>
                            {formatMessage(
                              inclusion === 'always'
                                ? templateListMessages.generationAlways
                                : templateListMessages.generationOnDemand,
                            )}
                          </span>
                        ) : (
                          <span className={styles.metaCell}>—</span>
                        )}
                      </td>
                    )}
                    <td className={cn(styles.tableCell, styles.centerCell)}>
                      <span
                        className={cn(
                          'p3b',
                          styles.statusBadge,
                          getStatusClassName(status),
                        )}
                      >
                        {getStatusLabel(status)}
                      </span>
                    </td>
                    <td className={cn(styles.tableCell, styles.typeCell)}>
                      <span className={cn('p3', styles.typeBadge)}>
                        {getDocumentTypeLabel(template.type)}
                      </span>
                      {(template.source_kind === 'system' ||
                        template.is_system) && (
                        <span className={cn('p3', styles.systemBadge)}>
                          {formatMessage(templateEditorMessages.systemBadge)}
                        </span>
                      )}
                      <span
                        className={styles.rowMenuSlot}
                        onClick={(event) => event.stopPropagation()}
                      >
                        <RadixPopover.Root>
                          <RadixPopover.Trigger asChild>
                            <button
                              type="button"
                              className={styles.rowMenuTrigger}
                              aria-label={formatMessage(
                                templateListMessages.actions,
                              )}
                            >
                              <Icon name="dots" size={24} />
                            </button>
                          </RadixPopover.Trigger>
                          <RadixPopover.Portal>
                            <RadixPopover.Content
                              className={styles.rowMenuContent}
                              sideOffset={8}
                              align="end"
                              collisionPadding={{ left: 16, right: 16 }}
                            >
                              <div className={styles.rowMenu}>
                                <div
                                  className={cn(styles.rowMenuItem, 'p2')}
                                  onClick={(event) =>
                                    handleEditTemplate(event, template)
                                  }
                                >
                                  <Icon name="pen" size={20} />
                                  <span>
                                    {formatMessage(templateListMessages.edit)}
                                  </span>
                                </div>
                                <div
                                  className={cn(
                                    styles.rowMenuItem,
                                    styles.rowMenuItemDanger,
                                    'p2',
                                  )}
                                  onClick={(event) =>
                                    handleDeleteTemplate(event, template)
                                  }
                                >
                                  <Icon name="delete" size={20} />
                                  <span>
                                    {formatMessage(templateListMessages.delete)}
                                  </span>
                                </div>
                              </div>
                            </RadixPopover.Content>
                          </RadixPopover.Portal>
                        </RadixPopover.Root>
                      </span>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
